/**
 * Admin Store Config — full CRUD for the Academy Store catalog + fulfillment orders.
 * Route: /admin/store-config
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Coins, Plus, Pencil, Trash2, Loader2, ArrowLeft, X, Package, Zap, Sparkles, Save, Truck } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { ITEM_ICONS } from '../components/storeIcons';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });
const EMPTY = { name: '', description: '', type: 'service', cost: 100, stock: '', digital_effect: '', active: true, icon: 'gift', image_url: '' };
const TYPE_ICON = { digital: Zap, physical: Package, service: Sparkles };

const AdminStoreConfig = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // item or 'new'
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/academy/admin/store-items', { headers: authHeaders() });
      setItems(r.data.items || []);
      const o = await api.get('/academy/admin/store-redemptions', { headers: authHeaders() });
      setOrders(o.data.redemptions || []);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) { toast.error('Admin access required'); navigate('/admin'); return; }
      toast.error('Could not load store config');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing('new'); };
  const openEdit = (it) => {
    setForm({ name: it.name, description: it.description || '', type: it.type, cost: it.cost,
      stock: it.stock == null ? '' : it.stock, digital_effect: it.digital_effect || '', active: it.active !== false, icon: it.icon || '', image_url: it.image_url || '' });
    setEditing(it);
  };

  const save = async () => {
    if (!form.name.trim() || !form.cost) { toast.error('Name and cost are required'); return; }
    setBusy(true);
    const payload = {
      name: form.name.trim(), description: form.description, type: form.type,
      cost: parseInt(form.cost, 10),
      stock: form.stock === '' ? null : parseInt(form.stock, 10),
      digital_effect: form.type === 'digital' ? (form.digital_effect || null) : null,
      active: !!form.active,
      icon: form.icon || null,
      image_url: form.image_url || null,
    };
    try {
      if (editing === 'new') await api.post('/academy/admin/store-items', payload, { headers: authHeaders() });
      else await api.put(`/academy/admin/store-items/${editing.id}`, payload, { headers: authHeaders() });
      toast.success('Saved'); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
    finally { setBusy(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.name}"?`)) return;
    try { await api.delete(`/academy/admin/store-items/${it.id}`, { headers: authHeaders() }); toast.success('Deleted'); load(); }
    catch { toast.error('Could not delete'); }
  };

  const toggleFulfill = async (o) => {
    const next = o.status === 'fulfilled' ? 'pending_fulfillment' : 'fulfilled';
    try { await api.put(`/academy/admin/store-redemptions/${o.id}`, { status: next }, { headers: authHeaders() }); load(); }
    catch { toast.error('Could not update'); }
  };

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-yellow-500" /></div>;

  return (
    <div className="min-h-screen bg-black text-white p-6" data-testid="admin-store-config">
      <div className="max-w-5xl mx-auto">
        <button onClick={() => navigate('/admin')} className="flex items-center gap-2 text-xs text-zinc-400 hover:text-white mb-4"><ArrowLeft className="w-4 h-4" /> Back to Admin</button>
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-yellow-500 font-black"><Coins className="w-3.5 h-3.5" /> Academy Store</div>
            <h1 className="text-3xl font-black">Store Config</h1>
          </div>
          <button onClick={openNew} className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-black px-4 py-2.5 text-xs font-black uppercase tracking-widest" data-testid="add-item-btn"><Plus className="w-4 h-4" /> Add Item</button>
        </div>

        {/* Items table */}
        <div className="border-2 border-zinc-800 divide-y divide-zinc-900">
          {items.map((it) => {
            const Icon = ITEM_ICONS[it.icon] || TYPE_ICON[it.type] || Zap;
            return (
              <div key={it.id} className="flex items-center gap-4 p-4" data-testid={`admin-item-${it.id}`}>
                <Icon className="w-5 h-5 text-yellow-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-white flex items-center gap-2">{it.name}
                    {it.active === false && <span className="text-[9px] uppercase tracking-widest text-zinc-500 border border-zinc-700 px-1.5 py-0.5">Hidden</span>}
                  </div>
                  <div className="text-[11px] text-zinc-500 truncate">{it.type} · {it.stock == null ? 'unlimited' : `${it.stock} in stock`}{it.digital_effect ? ` · effect: ${it.digital_effect}` : ''}</div>
                </div>
                <div className="flex items-center gap-1 text-yellow-300 font-black"><Coins className="w-4 h-4" />{it.cost}</div>
                <button onClick={() => openEdit(it)} className="p-2 border-2 border-zinc-800 hover:border-yellow-500 text-zinc-400 hover:text-white" data-testid={`edit-item-${it.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => remove(it)} className="p-2 border-2 border-zinc-800 hover:border-red-500 text-zinc-400 hover:text-red-400" data-testid={`delete-item-${it.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            );
          })}
          {items.length === 0 && <div className="p-6 text-center text-zinc-500 text-sm">No items yet — add your first.</div>}
        </div>

        {/* Fulfillment orders */}
        <h2 className="text-sm font-black uppercase tracking-[0.2em] text-zinc-400 mt-10 mb-3 flex items-center gap-2"><Truck className="w-4 h-4" /> Fulfillment Orders</h2>
        <div className="border-2 border-zinc-800 divide-y divide-zinc-900" data-testid="admin-orders">
          {orders.filter((o) => o.item_type !== 'digital').map((o) => (
            <div key={o.id} className="flex items-center gap-4 p-4" data-testid={`admin-order-${o.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-white">{o.item_name} <span className="text-[11px] text-zinc-500">· {o.student_name}</span></div>
                <div className="text-[11px] text-zinc-500 truncate">{(o.fulfillment?.name) || ''} · {(o.fulfillment?.address) || 'no address'} {o.fulfillment?.details ? `· ${o.fulfillment.details}` : ''}</div>
              </div>
              <button onClick={() => toggleFulfill(o)} className={`px-3 py-1.5 text-[10px] font-black uppercase tracking-widest ${o.status === 'fulfilled' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' : 'bg-amber-500/20 text-amber-400 border border-amber-500/40'}`} data-testid={`toggle-fulfill-${o.id}`}>
                {o.status === 'fulfilled' ? 'Fulfilled' : 'Mark Fulfilled'}
              </button>
            </div>
          ))}
          {orders.filter((o) => o.item_type !== 'digital').length === 0 && <div className="p-6 text-center text-zinc-500 text-sm">No fulfillment orders yet.</div>}
        </div>
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-zinc-950 border-2 border-yellow-500/40 p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="item-form-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="text-sm font-black uppercase tracking-widest">{editing === 'new' ? 'New Item' : 'Edit Item'}</h3><button onClick={() => setEditing(null)}><X className="w-4 h-4 text-zinc-500" /></button></div>
            <label className="block text-[9px] uppercase tracking-widest text-zinc-500 font-black mb-1">Name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-black border-2 border-zinc-800 px-3 py-2 text-sm outline-none focus:border-yellow-500 mb-3" data-testid="form-name" />
            <label className="block text-[9px] uppercase tracking-widest text-zinc-500 font-black mb-1">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="w-full bg-black border-2 border-zinc-800 px-3 py-2 text-sm outline-none focus:border-yellow-500 mb-3" data-testid="form-desc" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[9px] uppercase tracking-widest text-zinc-500 font-black mb-1">Type</label>
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full bg-black border-2 border-zinc-800 px-3 py-2 text-sm outline-none focus:border-yellow-500 mb-3" data-testid="form-type">
                  <option value="digital">Digital</option>
                  <option value="physical">Physical</option>
                  <option value="service">Service</option>
                </select>
              </div>
              <div>
                <label className="block text-[9px] uppercase tracking-widest text-zinc-500 font-black mb-1">Cost (Dollars)</label>
                <input type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="w-full bg-black border-2 border-zinc-800 px-3 py-2 text-sm outline-none focus:border-yellow-500 mb-3" data-testid="form-cost" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[9px] uppercase tracking-widest text-zinc-500 font-black mb-1">Stock (blank = ∞)</label>
                <input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="w-full bg-black border-2 border-zinc-800 px-3 py-2 text-sm outline-none focus:border-yellow-500 mb-3" data-testid="form-stock" />
              </div>
              {form.type === 'digital' && (
                <div>
                  <label className="block text-[9px] uppercase tracking-widest text-zinc-500 font-black mb-1">Digital Effect key</label>
                  <input value={form.digital_effect} onChange={(e) => setForm({ ...form, digital_effect: e.target.value })} placeholder="combine_streak_freeze" className="w-full bg-black border-2 border-zinc-800 px-3 py-2 text-sm outline-none focus:border-yellow-500 mb-3" data-testid="form-effect" />
                </div>
              )}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Icon</div>
            <div className="flex flex-wrap gap-1.5 mb-4" data-testid="form-icon-picker">
              {Object.entries(ITEM_ICONS).map(([k, I]) => (
                <button type="button" key={k} onClick={() => setForm({ ...form, icon: k })} title={k} className={`p-2 border-2 transition-colors ${form.icon === k ? 'border-yellow-500 bg-yellow-500/10 text-yellow-400' : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'}`} data-testid={`form-icon-${k}`}><I className="w-4 h-4" /></button>
              ))}
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-300 mb-4"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="form-active" /> Active (visible in store)</label>
            <button onClick={save} disabled={busy} className="w-full flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-black py-3 text-xs font-black tracking-[0.2em] uppercase" data-testid="form-save-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Save</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStoreConfig;
